import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ReactFlow, Background } from "@xyflow/react";
import { X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useMaps, usePins, useRegions, useEntity } from "@/lib/queries";
import { MapCanvas } from "@/components/MapCanvas";
import { PersonNode, RelEdge } from "@/components/relationships/GraphParts";
import { layoutNodes, typeMap } from "@/components/relationships/layout";
import { ListPick, Empty } from "@/components/studio/widgets/Pickers";

const PinPreview = ({ pin, onClose }) => {
  const { openEntity } = useOutletContext();
  const { data: e } = useEntity(pin.entity_id);
  return (
    <div className="absolute bottom-2 left-2 right-2 z-[500] max-h-[45%] overflow-y-auto rounded-md border border-[#2D3442] bg-[#13171F]/95 p-2.5 shadow-xl backdrop-blur" data-testid="map-widget-pin-preview">
      <button onClick={onClose} className="absolute right-1.5 top-1.5 text-[#A8A090] hover:text-[#F3EBDD]" data-testid="map-widget-pin-close"><X className="h-3.5 w-3.5" /></button>
      <div className="pr-5 font-display text-base text-[#F3EBDD]">{e?.name || pin.name || pin.label || "Pin"}</div>
      {e?.summary && <p className="mt-0.5 line-clamp-2 text-[11px] text-[#A8A090]">{e.summary}</p>}
      {pin.entity_id && <button onClick={() => openEntity?.(pin.entity_id)} className="mt-1 flex items-center gap-1 text-[11px] text-[#F59E0B] hover:underline" data-testid="map-widget-open-note"><ExternalLink className="h-3 w-3" />Open note</button>}
    </div>
  );
};

export const MapWidget = ({ uid, cfg, set }) => {
  const { data: maps = [] } = useMaps(uid);
  const map = maps.find((m) => m.id === cfg.id) || (cfg.id ? null : maps[0]);
  const { data: pins = [] } = usePins(map?.id);
  const { data: regions = [] } = useRegions(map?.id);
  const [pin, setPin] = useState(null);
  if (!maps.length) return <Empty testid="map-widget-empty">No maps yet.</Empty>;
  return (
    <div className="flex h-full flex-col gap-2" data-testid="map-widget">
      <ListPick items={maps} value={map?.id} onChange={(id) => { set({ id }); setPin(null); }} testid="map-widget-pick" placeholder="Map" />
      {map && (
        <div className="relative min-h-[200px] flex-1 overflow-hidden rounded border border-[#2D3442]">
          <MapCanvas refit map={map} pins={pins} regions={regions} onMapClick={() => setPin(null)} onPinClick={setPin} />
          {pin && <PinPreview pin={pin} onClose={() => setPin(null)} />}
        </div>
      )}
    </div>
  );
};

const nodeTypes = { person: PersonNode };
const edgeTypes = { rel: RelEdge };

export const RelationsWidget = ({ uid, mode }) => {
  const { openEntity } = useOutletContext();
  const { data } = useQuery({ queryKey: ["rel-web", uid], queryFn: () => api.get(`/universes/${uid}/relationship-web`).then((r) => r.data) });
  const graph = useMemo(() => {
    if (!data) return { nodes: [], edges: [] };
    const tmap = typeMap(data.types || []);
    const byId = Object.fromEntries(data.nodes.map((n) => [n.id, n]));
    const pos = mode === "tree" ? Object.fromEntries(Object.entries(Object.assign({}, ...data.trees.map((t) => t.pos))).map(([k, [x, y]]) => [k, { x, y }])) : layoutNodes(data.nodes, data.edges);
    const nodes = Object.keys(pos).filter((id) => byId[id]).map((id) => ({ id, type: "person", position: pos[id], draggable: false, data: { ...byId[id], canEdit: false } }));
    const edges = data.edges.filter((e) => pos[e.a] && pos[e.b] && (mode !== "tree" || ["family", "marriage", "love"].includes(e.type))).map((e) => {
      const t = tmap[e.type] || { color: "#A8A090", label: e.type };
      return { id: e.id, source: e.a, target: e.b, type: "rel", data: { color: t.color, label: t.label, status: e.status, offset: 0, onClick: () => {} } };
    });
    return { nodes, edges };
  }, [data, mode]);
  if (!data) return <Empty>Loading…</Empty>;
  if (!graph.nodes.length) return <Empty testid={`rel-widget-empty-${mode}`}>{mode === "tree" ? "No family links yet." : "No relationships yet."}</Empty>;
  return (
    <div className="h-full min-h-[220px] overflow-hidden rounded border border-[#2D3442]" data-testid={`rel-widget-${mode}`}>
      <ReactFlow nodes={graph.nodes} edges={graph.edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView fitViewOptions={{ maxZoom: 1, padding: 0.2 }} minZoom={0.1} nodesDraggable={false} nodesConnectable={false}
        onNodeClick={(_, n) => openEntity?.(n.id)} proOptions={{ hideAttribution: true }}>
        <Background color="#2D3442" gap={24} />
      </ReactFlow>
    </div>
  );
};
